import React, { useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, Lightbulb, AlertTriangle, CheckCircle, Target, Monitor, Globe, Search, Layers, BarChart2 } from 'lucide-react';
import ModuleVisibilityToggle from '../ModuleVisibilityToggle';

/**
 * InsightCard - Carte d'insight avec tooltip au survol
 */
const InsightCard = ({ insight, style }) => {
  const [isHovered, setIsHovered] = useState(false);
  const IconComponent = insight.icon;
  const tooltipText = insight.details ? insight.details.join('\n') : '';

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: style.bg,
        border: `1px solid ${style.border}`,
        borderRadius: '8px',
        padding: '12px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        overflow: 'visible',
        minWidth: 0,
        cursor: 'default',
        position: 'relative'
      }}
    >
      <IconComponent size={18} style={{ color: style.icon, flexShrink: 0, marginTop: '2px' }} />
      <div style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
        <div style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937', marginBottom: '2px', wordBreak: 'break-word' }}>
          {insight.title}
        </div>
        <div style={{ fontSize: '12px', color: '#4b5563', wordBreak: 'break-word' }}>
          {insight.text}
        </div>
      </div>
      {/* Tooltip */}
      {isHovered && insight.details && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: '8px',
          backgroundColor: style.bg,
          border: `1px solid ${style.border}`,
          color: '#374151',
          padding: '10px 12px',
          borderRadius: '8px',
          fontSize: '11px',
          lineHeight: '1.6',
          whiteSpace: 'pre-line',
          zIndex: 9999,
          minWidth: '220px',
          maxWidth: '320px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
        }}>
          {/* Flèche du tooltip */}
          <div style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderBottom: `8px solid ${style.border}`
          }} />
          <div style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '-1px',
            width: 0,
            height: 0,
            borderLeft: '7px solid transparent',
            borderRight: '7px solid transparent',
            borderBottom: `7px solid ${style.bg}`
          }} />
          {insight.details.map((detail, i) => (
            <div key={i} style={{ marginBottom: i < insight.details.length - 1 ? '4px' : 0 }}>
              {detail}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * MicrosoftAdsExecutiveSummary - Composant d'analyse (format identique à GA4InsightsPanel)
 * Affiche des cartes colorées avec insights et détails au survol
 */
const ExecutiveSummary = ({
  campaignsData = [],
  adGroupsData = [],
  deviceData = [],
  networkData = [],
  searchTermsData = [],
  previousPeriodData = null,
  compareEnabled = false,
  comparisonType = '',
  formatNumber,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'msads_analysis'
}) => {
  const [isVisible, setIsVisible] = useState(true);

  // Calcul des métriques globales
  const globalMetrics = useMemo(() => {
    if (!campaignsData.length) return null;

    const totals = campaignsData.reduce((acc, campaign) => ({
      impressions: acc.impressions + (campaign.impressions || 0),
      clicks: acc.clicks + (campaign.clicks || 0),
      cost: acc.cost + (campaign.cost || 0),
      conversions: acc.conversions + (campaign.conversions || 0),
      conversionsValue: acc.conversionsValue + (campaign.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    return {
      ...totals,
      ctr: totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0,
      cpc: totals.clicks > 0 ? (totals.cost / totals.clicks) : 0,
      conversionRate: totals.clicks > 0 ? (totals.conversions / totals.clicks * 100) : 0,
      cpa: totals.conversions > 0 ? (totals.cost / totals.conversions) : 0,
      roas: totals.cost > 0 ? (totals.conversionsValue / totals.cost) : 0,
      cos: totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue * 100) : 0
    };
  }, [campaignsData]);

  // Générer les insights KPIs
  const kpiInsights = useMemo(() => {
    // Ne pas générer d'insights de comparaison si la comparaison n'est pas activée
    if (!compareEnabled) return [];

    const prev = previousPeriodData?.totals || previousPeriodData;
    if (!globalMetrics || !prev || !prev.totalCost) return [];

    const insights = [];
    const prevData = {
      cost: prev.totalCost || prev.cost || 0,
      conversions: prev.totalConversions || prev.conversions || 0,
      conversionsValue: prev.totalConversionsValue || prev.conversionsValue || 0,
      clicks: prev.totalClicks || prev.clicks || 0,
      impressions: prev.totalImpressions || prev.impressions || 0
    };

    const calcChange = (current, previous) => {
      if (!previous || previous === 0) return null;
      return ((current - previous) / previous * 100);
    };

    const conversionsChange = calcChange(globalMetrics.conversions, prevData.conversions);
    const costChange = calcChange(globalMetrics.cost, prevData.cost);
    const prevRoas = prevData.cost > 0 ? (prevData.conversionsValue / prevData.cost) : 0;
    const roasChange = calcChange(globalMetrics.roas, prevRoas);
    const prevCpa = prevData.conversions > 0 ? (prevData.cost / prevData.conversions) : 0;
    const cpaChange = calcChange(globalMetrics.cpa, prevCpa);
    const clicksChange = calcChange(globalMetrics.clicks, prevData.clicks);

    // Insight conversions
    if (conversionsChange !== null) {
      if (conversionsChange > 10) {
        insights.push({
          type: 'positive',
          icon: TrendingUp,
          title: 'Conversions en hausse',
          text: `+${formatNumber(conversionsChange, 1)}% vs période précédente`,
          details: [
            `Conversions actuelles : ${formatNumber(globalMetrics.conversions)}`,
            `Conversions précédentes : ${formatNumber(prevData.conversions)}`,
            `Valeur totale : ${formatNumber(globalMetrics.conversionsValue, 2)}€`,
            costChange !== null ? `Budget ${costChange > 0 ? '+' : ''}${formatNumber(costChange, 1)}%` : null
          ].filter(Boolean)
        });
      } else if (conversionsChange < -10) {
        insights.push({
          type: 'negative',
          icon: TrendingDown,
          title: 'Conversions en baisse',
          text: `${formatNumber(conversionsChange, 1)}% vs période précédente`,
          details: [
            `Conversions actuelles : ${formatNumber(globalMetrics.conversions)}`,
            `Conversions précédentes : ${formatNumber(prevData.conversions)}`,
            `Clics ${clicksChange !== null ? (clicksChange > 0 ? '+' : '') + formatNumber(clicksChange, 1) + '%' : 'N/A'}`,
            `Taux de conversion : ${formatNumber(globalMetrics.conversionRate, 2)}%`
          ]
        });
      }
    }

    // Insight ROAS
    if (roasChange !== null && prevRoas > 0) {
      if (roasChange > 10) {
        insights.push({
          type: 'positive',
          icon: Target,
          title: 'ROAS en amélioration',
          text: `+${formatNumber(roasChange, 1)}% (${formatNumber(globalMetrics.roas, 2)}x)`,
          details: [
            `ROAS actuel : ${formatNumber(globalMetrics.roas, 2)}x`,
            `ROAS précédent : ${formatNumber(prevRoas, 2)}x`,
            `Revenus : ${formatNumber(globalMetrics.conversionsValue, 2)}€`,
            `Dépenses : ${formatNumber(globalMetrics.cost, 2)}€`
          ]
        });
      } else if (roasChange < -10) {
        insights.push({
          type: 'warning',
          icon: AlertTriangle,
          title: 'ROAS en baisse',
          text: `${formatNumber(roasChange, 1)}% (${formatNumber(globalMetrics.roas, 2)}x)`,
          details: [
            `ROAS actuel : ${formatNumber(globalMetrics.roas, 2)}x`,
            `ROAS précédent : ${formatNumber(prevRoas, 2)}x`,
            `Perte de rentabilité : ${formatNumber((prevRoas - globalMetrics.roas) * globalMetrics.cost, 2)}€`,
            `Action : Analyser les campagnes sous-performantes`
          ]
        });
      }
    }

    // Insight CPA
    if (cpaChange !== null && prevCpa > 0 && globalMetrics.cpa < Infinity) {
      if (cpaChange < -10) {
        insights.push({
          type: 'positive',
          icon: CheckCircle,
          title: 'CPA en amélioration',
          text: `${formatNumber(Math.abs(cpaChange), 1)}% de réduction`,
          details: [
            `CPA actuel : ${formatNumber(globalMetrics.cpa, 2)}€`,
            `CPA précédent : ${formatNumber(prevCpa, 2)}€`,
            `Économie par conversion : ${formatNumber(prevCpa - globalMetrics.cpa, 2)}€`,
            `Total économisé : ${formatNumber((prevCpa - globalMetrics.cpa) * globalMetrics.conversions, 2)}€`
          ]
        });
      } else if (cpaChange > 10) {
        insights.push({
          type: 'warning',
          icon: AlertTriangle,
          title: 'CPA en hausse',
          text: `+${formatNumber(cpaChange, 1)}% (${formatNumber(globalMetrics.cpa, 2)}€)`,
          details: [
            `CPA actuel : ${formatNumber(globalMetrics.cpa, 2)}€`,
            `CPA précédent : ${formatNumber(prevCpa, 2)}€`,
            `Surcoût par conversion : ${formatNumber(globalMetrics.cpa - prevCpa, 2)}€`,
            `Action : Optimiser le ciblage et les enchères`
          ]
        });
      }
    }

    return insights;
  }, [globalMetrics, previousPeriodData, formatNumber, compareEnabled]);

  // Analyse des campagnes
  const campaignInsights = useMemo(() => {
    if (!campaignsData.length || !globalMetrics) return [];

    const insights = [];
    const avgCpa = globalMetrics.cpa;

    const convertingCampaigns = campaignsData
      .filter(c => c.conversions > 0 && c.cost > 0)
      .map(c => ({
        ...c,
        roas: c.conversionsValue / c.cost,
        cpa: c.cost / c.conversions
      }))
      .sort((a, b) => b.roas - a.roas);

    // Top campagne
    if (convertingCampaigns.length > 0) {
      const top = convertingCampaigns[0];
      const topName = top.name || top.campaignName;
      insights.push({
        type: 'positive',
        icon: Target,
        title: 'Meilleure campagne',
        text: `${topName} (ROAS ${formatNumber(top.roas, 2)}x)`,
        details: [
          `Campagne : ${topName}`,
          `ROAS : ${formatNumber(top.roas, 2)}x`,
          `Conversions : ${formatNumber(top.conversions)}`,
          `CPA : ${formatNumber(top.cpa, 2)}€`,
          `Dépenses : ${formatNumber(top.cost, 2)}€`
        ]
      });
    }

    // Campagnes sans conversions
    const noConversions = campaignsData.filter(c => c.cost > 10 && c.conversions === 0);
    if (noConversions.length > 0) {
      const totalWaste = noConversions.reduce((sum, c) => sum + c.cost, 0);
      const topWasted = [...noConversions].sort((a, b) => b.cost - a.cost).slice(0, 3);
      insights.push({
        type: 'warning',
        icon: AlertTriangle,
        title: `${noConversions.length} campagne(s) sans conversion`,
        text: `${formatNumber(totalWaste, 2)}€ dépensés sans résultat`,
        details: [
          `Budget gaspillé : ${formatNumber(totalWaste, 2)}€`,
          `Campagnes concernées :`,
          ...topWasted.map(c => `• ${c.name || c.campaignName} (${formatNumber(c.cost, 2)}€)`),
          noConversions.length > 3 ? `... et ${noConversions.length - 3} autres` : null
        ].filter(Boolean)
      });
    }

    // Campagnes avec CPA élevé
    const highCPA = convertingCampaigns.filter(c => c.cpa > avgCpa * 1.5);
    if (highCPA.length > 0) {
      const topHighCPA = [...highCPA].sort((a, b) => b.cpa - a.cpa).slice(0, 3);
      insights.push({
        type: 'warning',
        icon: Lightbulb,
        title: `${highCPA.length} campagne(s) CPA élevé`,
        text: `CPA > 50% de la moyenne (${formatNumber(avgCpa, 2)}€)`,
        details: [
          `CPA moyen compte : ${formatNumber(avgCpa, 2)}€`,
          `Seuil d'alerte : ${formatNumber(avgCpa * 1.5, 2)}€`,
          `Campagnes à optimiser :`,
          ...topHighCPA.map(c => `• ${c.name || c.campaignName} (CPA ${formatNumber(c.cpa, 2)}€)`)
        ]
      });
    }

    return insights;
  }, [campaignsData, globalMetrics, formatNumber]);

  // Analyse des groupes d'annonces
  const adGroupInsights = useMemo(() => {
    if (!adGroupsData.length || !globalMetrics) return [];

    const insights = [];
    const avgCpa = globalMetrics.cpa;

    const convertingAdGroups = adGroupsData
      .filter(ag => ag.conversions > 0 && ag.cost > 0)
      .map(ag => ({
        ...ag,
        roas: ag.conversionsValue / ag.cost,
        cpa: ag.cost / ag.conversions
      }))
      .sort((a, b) => b.roas - a.roas);

    // Top groupe d'annonces
    if (convertingAdGroups.length > 0) {
      const top = convertingAdGroups[0];
      const topName = top.name || top.adGroupName;
      insights.push({
        type: 'positive',
        icon: Layers,
        title: 'Meilleur groupe d\'annonces',
        text: `${topName} (ROAS ${formatNumber(top.roas, 2)}x)`,
        details: [
          `Groupe : ${topName}`,
          `ROAS : ${formatNumber(top.roas, 2)}x`,
          `Conversions : ${formatNumber(top.conversions)}`,
          `CPA : ${formatNumber(top.cpa, 2)}€`,
          `Dépenses : ${formatNumber(top.cost, 2)}€`
        ]
      });
    }

    // Groupes sans conversions avec dépenses significatives
    const noConversions = adGroupsData.filter(ag => ag.cost > 10 && ag.conversions === 0);
    if (noConversions.length > 0) {
      const totalWaste = noConversions.reduce((sum, ag) => sum + ag.cost, 0);
      const topWasted = [...noConversions].sort((a, b) => b.cost - a.cost).slice(0, 3);
      insights.push({
        type: 'warning',
        icon: Layers,
        title: `${noConversions.length} groupe(s) sans conversion`,
        text: `${formatNumber(totalWaste, 2)}€ dépensés sans résultat`,
        details: [
          `Budget gaspillé : ${formatNumber(totalWaste, 2)}€`,
          `Groupes concernés :`,
          ...topWasted.map(ag => `• ${ag.name || ag.adGroupName} (${formatNumber(ag.cost, 2)}€)`),
          noConversions.length > 3 ? `... et ${noConversions.length - 3} autres` : null
        ].filter(Boolean)
      });
    }

    // Groupes avec CPA élevé
    const highCPA = convertingAdGroups.filter(ag => ag.cpa > avgCpa * 1.5);
    if (highCPA.length > 0) {
      const topHighCPA = [...highCPA].sort((a, b) => b.cpa - a.cpa).slice(0, 3);
      insights.push({
        type: 'warning',
        icon: Layers,
        title: `${highCPA.length} groupe(s) CPA élevé`,
        text: `CPA > 50% de la moyenne (${formatNumber(avgCpa, 2)}€)`,
        details: [
          `CPA moyen compte : ${formatNumber(avgCpa, 2)}€`,
          `Seuil d'alerte : ${formatNumber(avgCpa * 1.5, 2)}€`,
          `Groupes à optimiser :`,
          ...topHighCPA.map(ag => `• ${ag.name || ag.adGroupName} (CPA ${formatNumber(ag.cpa, 2)}€)`)
        ]
      });
    }

    return insights;
  }, [adGroupsData, globalMetrics, formatNumber]);

  // Analyse des appareils
  const deviceInsights = useMemo(() => {
    if (!deviceData.length) return [];

    const insights = [];
    const devicesWithMetrics = deviceData
      .filter(d => d.clicks > 0)
      .map(d => ({
        ...d,
        deviceName: d.device === 'Computer' ? 'Ordinateur' : d.device === 'Smartphone' ? 'Mobile' : d.device === 'Tablet' ? 'Tablette' : d.device,
        roas: d.cost > 0 ? (d.conversionsValue / d.cost) : 0,
        cpa: d.conversions > 0 ? (d.cost / d.conversions) : Infinity,
        convRate: d.clicks > 0 ? (d.conversions / d.clicks * 100) : 0
      }))
      .sort((a, b) => b.roas - a.roas);

    if (devicesWithMetrics.length > 1) {
      const best = devicesWithMetrics[0];
      const worst = devicesWithMetrics[devicesWithMetrics.length - 1];

      if (best.roas > 0) {
        insights.push({
          type: 'info',
          icon: Monitor,
          title: `${best.deviceName} performe le mieux`,
          text: `ROAS ${formatNumber(best.roas, 2)}x`,
          details: [
            `Appareil : ${best.deviceName}`,
            `ROAS : ${formatNumber(best.roas, 2)}x`,
            `Conversions : ${formatNumber(best.conversions)}`,
            `Taux de conversion : ${formatNumber(best.convRate, 2)}%`,
            `Part du budget : ${formatNumber(best.cost / devicesWithMetrics.reduce((s, d) => s + d.cost, 0) * 100, 1)}%`
          ]
        });
      }

      if (worst.roas > 0 && worst.roas < best.roas * 0.5) {
        insights.push({
          type: 'warning',
          icon: Monitor,
          title: `${worst.deviceName} à optimiser`,
          text: `ROAS ${formatNumber(worst.roas, 2)}x seulement`,
          details: [
            `Appareil : ${worst.deviceName}`,
            `ROAS : ${formatNumber(worst.roas, 2)}x`,
            `vs meilleur : ${formatNumber(best.roas, 2)}x (${best.deviceName})`,
            `Taux de conversion : ${formatNumber(worst.convRate, 2)}%`,
            `Action : Ajuster les enchères par appareil`
          ]
        });
      }
    }

    return insights;
  }, [deviceData, formatNumber]);

  // Analyse des réseaux
  const networkInsights = useMemo(() => {
    if (!networkData.length) return [];

    const insights = [];
    const totalCost = networkData.reduce((sum, n) => sum + (n.cost || 0), 0);

    const networksWithMetrics = networkData
      .filter(n => n.cost > 0)
      .map(n => ({
        ...n,
        roas: n.conversionsValue > 0 ? (n.conversionsValue / n.cost) : 0,
        costShare: totalCost > 0 ? (n.cost / totalCost * 100) : 0,
        cpa: n.conversions > 0 ? (n.cost / n.conversions) : Infinity
      }))
      .sort((a, b) => b.roas - a.roas);

    if (networksWithMetrics.length > 0) {
      const best = networksWithMetrics[0];
      if (best.roas > 0) {
        insights.push({
          type: 'positive',
          icon: Globe,
          title: `${best.networkName} : meilleur réseau`,
          text: `ROAS ${formatNumber(best.roas, 2)}x (${formatNumber(best.costShare, 1)}% du budget)`,
          details: [
            `Réseau : ${best.networkName}`,
            `ROAS : ${formatNumber(best.roas, 2)}x`,
            `Conversions : ${formatNumber(best.conversions)}`,
            `CPA : ${best.cpa < Infinity ? formatNumber(best.cpa, 2) + '€' : 'N/A'}`,
            `Part du budget : ${formatNumber(best.costShare, 1)}%`
          ]
        });
      }

      // Réseau sous-performant
      if (networksWithMetrics.length > 1) {
        const worst = networksWithMetrics[networksWithMetrics.length - 1];
        if (worst.costShare > 10 && worst.roas < best.roas * 0.5) {
          insights.push({
            type: 'warning',
            icon: Globe,
            title: `${worst.networkName} sous-performe`,
            text: `ROAS ${formatNumber(worst.roas, 2)}x (${formatNumber(worst.costShare, 1)}% du budget)`,
            details: [
              `Réseau : ${worst.networkName}`,
              `ROAS : ${formatNumber(worst.roas, 2)}x`,
              `vs meilleur : ${formatNumber(best.roas, 2)}x`,
              `Budget concerné : ${formatNumber(worst.cost, 2)}€`,
              `Action : Réallouer le budget vers ${best.networkName}`
            ]
          });
        }
      }
    }

    return insights;
  }, [networkData, formatNumber]);

  // Analyse des termes de recherche
  const searchTermsInsights = useMemo(() => {
    if (!searchTermsData.length) return [];

    const insights = [];

    const convertingTerms = searchTermsData
      .filter(t => t.conversions > 0 && t.cost > 0)
      .map(t => ({
        ...t,
        term: t.searchTerm || t.query || t.keyword,
        roas: t.conversionsValue / t.cost,
        cpa: t.cost / t.conversions
      }))
      .sort((a, b) => b.roas - a.roas);

    if (convertingTerms.length > 0) {
      const top = convertingTerms[0];
      const top3 = convertingTerms.slice(0, 3);
      insights.push({
        type: 'positive',
        icon: Search,
        title: 'Meilleur terme de recherche',
        text: `"${top.term}" (ROAS ${formatNumber(top.roas, 2)}x)`,
        details: [
          `Top 3 termes performants :`,
          ...top3.map((t, i) => `${i + 1}. "${t.term}" - ROAS ${formatNumber(t.roas, 2)}x`),
          ``,
          `Action : Augmenter les enchères sur ces termes`
        ]
      });
    }

    const wastedTerms = searchTermsData.filter(t => t.cost > 5 && t.conversions === 0);
    if (wastedTerms.length > 0) {
      const totalWaste = wastedTerms.reduce((sum, t) => sum + t.cost, 0);
      const topWasted = [...wastedTerms].sort((a, b) => b.cost - a.cost).slice(0, 3);
      insights.push({
        type: 'warning',
        icon: Search,
        title: `${wastedTerms.length} terme(s) sans conversion`,
        text: `${formatNumber(totalWaste, 2)}€ dépensés`,
        details: [
          `Budget gaspillé : ${formatNumber(totalWaste, 2)}€`,
          `Termes les plus coûteux :`,
          ...topWasted.map(t => `• "${t.searchTerm || t.query || t.keyword}" (${formatNumber(t.cost, 2)}€)`),
          ``,
          `Action : Ajouter en mots-clés négatifs`
        ]
      });
    }

    return insights;
  }, [searchTermsData, formatNumber]);

  // Combiner tous les insights
  const allInsights = [...kpiInsights, ...campaignInsights, ...adGroupInsights, ...deviceInsights, ...networkInsights, ...searchTermsInsights];

  if (!globalMetrics || allInsights.length === 0) {
    return null;
  }

  // Vérification de visibilité pour les clients
  const clientModuleVisible = isClientModuleVisible ? isClientModuleVisible(moduleId) : true;
  if (userRole === 'CLIENT' && !clientModuleVisible) {
    return null;
  }

  const getInsightStyle = (type) => {
    switch (type) {
      case 'positive':
        return { bg: '#ecfdf5', border: '#10b981', icon: '#059669' };
      case 'negative':
        return { bg: '#fef2f2', border: '#ef4444', icon: '#dc2626' };
      case 'warning':
        return { bg: '#fffbeb', border: '#f59e0b', icon: '#d97706' };
      default:
        return { bg: '#FAF9F5', border: '#1a1a1a', icon: '#333333' };
    }
  };

  const sectionStyle = {
    backgroundColor: '#FAF9F5',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    overflow: 'visible'
  };

  return (
    <div style={sectionStyle}>
      <div className="section-header">
        <h4 style={{ margin: 0, color: '#1f2937', fontSize: '1.2em', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}><BarChart2 size={20} /> Analyse</h4>
        {toggleClientModuleVisibility ? (
          <ModuleVisibilityToggle
            isVisible={clientModuleVisible}
            onToggle={toggleClientModuleVisibility}
            userRole={userRole}
            moduleId={moduleId}
            title="Analyse Microsoft Ads"
          />
        ) : (
          <button
            onClick={() => setIsVisible(!isVisible)}
            className="section-toggle-btn"
            title={isVisible ? "Masquer la section" : "Afficher la section"}
          >
            {isVisible ? '−' : '+'}
          </button>
        )}
      </div>

      {(userRole !== 'CLIENT' || clientModuleVisible) && isVisible && (
        <div>
          {allInsights.length > 0 && (
            <div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '12px',
                maxWidth: '100%'
              }}>
                {allInsights.slice(0, 10).map((insight, index) => (
                  <InsightCard
                    key={index}
                    insight={insight}
                    style={getInsightStyle(insight.type)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExecutiveSummary;
